import React from 'react'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet'

import { makeStyles } from '@material-ui/core/styles'
import { getParcelBounds } from '../../../../../utils/map'
import LeafletLayersControl from '../../../LeafletLayersControl'
import ManualClusteringGeoman from '../ManualClusteringGeoman'

// Center map on Granada
const position = [37.741934, -3.188515]

const useStyles = makeStyles((theme) => ({
  root: { flexGrow: 1 },
  loading: {
    marginBottom: theme.spacing(20),
    marginTop: theme.spacing(20),
  },
}))

function ColoredZoneLayer({ data, opacity = 1, name }) {
  const groupRef = React.useRef()
  const map = useMap()
  const { t } = useTranslation()

  const onEachFeature = (feature, layer) => {
    let tooltipContent = ''
    if (feature.properties?.mean && feature.id) {
      tooltipContent = `<b>${t('zone', 'Zone')} ${feature.id}</b><br/>Mean: ${feature.properties.mean.toFixed(2)}`
    } else if (feature.properties?.zone && feature.properties?.mean) {
      tooltipContent = `<b>${t('zone', 'Zone')} ${feature.properties.zone}</b><br/>Mean: ${feature.properties.mean.toFixed(2)}`
    }

    if (tooltipContent) {
      layer.bindTooltip(tooltipContent)
    }

    if (feature.properties?.color) {
      layer.setStyle({
        color: feature.properties.color,
        fillColor: feature.properties.color,
        weight: 1,
        fillOpacity: opacity,
      })
    } else {
      layer.setStyle({
        color: 'grey',
        fillColor: 'lightgrey',
        weight: 2,
        fillOpacity: opacity * 0.7,
      })
    }
  }

  React.useEffect(() => {
    if (map && groupRef.current && data) {
      const currentLayer = groupRef.current
      if (typeof currentLayer.getLayers === 'function' && currentLayer.getLayers().length > 0) {
        const bounds = currentLayer.getBounds()
        if (bounds && typeof bounds.isValid === 'function' && bounds.isValid()) {
          map.fitBounds(bounds)
        }
      }
    }
  }, [map, data])

  React.useEffect(() => {
    if (groupRef.current) {
      groupRef.current.clearLayers()
      if (data) {
        groupRef.current.addData(data)
      }
    }
  }, [data])

  if (!data) return null

  const layerKey =
    name || (data && data.features ? `geojson-${data.features.length}` : 'geojson-layer')

  return <GeoJSON key={layerKey} data={data} onEachFeature={onEachFeature} ref={groupRef} />
}

export default function FertilizerPerformanceMap({ parcel, map, setMap, zonedGeom, setZonedGeom }) {
  const classes = useStyles()
  const { t } = useTranslation()
  const [manualMode] = React.useState(false)

  const [activeBaseLayer, setActiveBaseLayer] = React.useState('Esri World Imagery')
  const [overlays, setOverlays] = React.useState({
    'Zoned performance map': true,
    Parcel: true,
  })

  React.useEffect(() => {
    if (map) map.fitBounds(getParcelBounds(parcel))
  }, [map, parcel])

  const baseLayers = [
    { name: 'Esri World Imagery', checked: activeBaseLayer === 'Esri World Imagery' },
    { name: 'CartoDB Voyager', checked: activeBaseLayer === 'CartoDB Voyager' },
  ]

  const overlayGroups = [
    {
      name: 'Performance',
      overlays: [
        ...(zonedGeom
          ? [{ name: 'Zoned performance map', visible: overlays['Zoned performance map'] }]
          : []),
        { name: 'Parcel', visible: overlays['Parcel'] },
      ],
    },
  ]

  const handleOverlayToggle = (name, visible) =>
    setOverlays((prev) => ({ ...prev, [name]: visible }))

  return (
    <MapContainer
      center={position}
      minZoom={2}
      maxZoom={20}
      zoom={9}
      style={{ width: '100%', height: 400, borderRadius: 4 }}
      zoomControl={false}
      whenCreated={setMap}
    >
      {activeBaseLayer === 'Esri World Imagery' && (
        <TileLayer
          maxZoom={20}
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
      )}
      {activeBaseLayer === 'CartoDB Voyager' && (
        <TileLayer
          maxZoom={20}
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        />
      )}

      {zonedGeom && overlays['Zoned performance map'] && (
        <ColoredZoneLayer data={zonedGeom} name="Zoned Parcel" opacity={1} />
      )}
      {overlays['Parcel'] && <GeoJSON data={parcel} color="yellow" name="Zones" />}

      <LeafletLayersControl
        baseLayers={baseLayers}
        overlayGroups={overlayGroups}
        onBaseLayerChange={setActiveBaseLayer}
        onOverlayToggle={handleOverlayToggle}
        top={12}
        right={12}
      />

      {manualMode && zonedGeom && (
        <ManualClusteringGeoman zonedGeom={zonedGeom} setZonedGeom={setZonedGeom} />
      )}
    </MapContainer>
  )
}
